/**
 * Gemini Live API session manager.
 *
 * Manages the WebSocket lifecycle between the browser and the FastAPI proxy.
 * Handles:
 *   - Connecting / disconnecting
 *   - Sending PCM audio chunks as base64-encoded JSON realtimeInput messages
 *   - Receiving and dispatching server messages (audio, transcripts, status, errors)
 *   - Auto-reconnect with exponential backoff
 *   - Preserving the MediaStream (mic) across reconnects
 */

import { AudioCapture } from "./audioCapture";
import { AudioPlayback } from "./audioPlayback";
import { getBackendWsBase } from "./backendBase";

export type SessionState =
  | "idle"
  | "loading_docs"
  | "connecting"
  | "live"
  | "ai_speaking"
  | "reconnecting"
  | "error"
  | "ended";

export interface TranscriptEntry {
  role: "user" | "agent";
  text: string;
  timestamp: number;
}

export interface SessionCallbacks {
  onStateChange: (state: SessionState) => void;
  onTranscript: (entry: TranscriptEntry) => void;
  onInputRms: (rms: number) => void;
  onOutputRms: (rms: number) => void;
  onError: (message: string) => void;
}

const MAX_RECONNECT_ATTEMPTS = 5;
const BASE_RECONNECT_DELAY_MS = 1000;

export class GeminiSession {
  private ws: WebSocket | null = null;
  private capture: AudioCapture;
  private playback: AudioPlayback;
  private agentId: string;
  private callbacks: SessionCallbacks;
  private state: SessionState = "idle";
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;

  constructor(agentId: string, callbacks: SessionCallbacks) {
    this.agentId = agentId;
    this.callbacks = callbacks;

    this.capture = new AudioCapture({
      onChunk: (pcm) => this._sendAudioChunk(pcm),
      onRms: (rms) => callbacks.onInputRms(rms),
    });

    this.playback = new AudioPlayback({
      onAiSpeakingStart: () => this._setState("ai_speaking"),
      onAiSpeakingStop: () => {
        if (this.state === "ai_speaking") this._setState("live");
      },
      onTurnComplete: () => {
        // Both server turn-done AND audio queue empty → ready for next turn
        if (this.state === "ai_speaking") this._setState("live");
      },
      onOutputRms: (rms) => callbacks.onOutputRms(rms),
    });
  }

  /** Start capturing mic and open the WebSocket session. */
  async start(): Promise<void> {
    this.stopped = false;
    this.reconnectAttempts = 0;
    await this.capture.start();
    this._connect();
  }

  /** Gracefully stop the session. */
  stop(): void {
    this.stopped = true;
    this.capture.stopCapture();
    this.playback.interrupt();
    this._closeWs();
    this._setState("ended");
  }

  /** Full teardown — call on component unmount. */
  destroy(): void {
    this.stop();
    this.capture.destroy();
    this.playback.destroy();
  }

  get currentState(): SessionState {
    return this.state;
  }

  // ── Private ──────────────────────────────────────────────────────────────

  private _connect(): void {
    if (this.stopped) return;

    const secret = process.env.NEXT_PUBLIC_WS_SECRET?.trim();
    const qs = secret ? `?secret=${encodeURIComponent(secret)}` : "";
    const url = `${getBackendWsBase()}/ws/${this.agentId}${qs}`;
    this._setState("loading_docs");
    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      this.reconnectAttempts = 0;
      this._setState("connecting");
    };

    this.ws.onmessage = (event) => this._handleMessage(event.data as string);

    this.ws.onerror = () => {
      this.callbacks.onError("WebSocket error — retrying…");
    };

    this.ws.onclose = (ev) => {
      if (this.stopped) return;
      if (ev.code === 1000 || ev.code === 1001) {
        this._setState("ended");
        return;
      }
      this._scheduleReconnect();
    };
  }

  private _handleMessage(raw: string): void {
    let msg: Record<string, unknown>;
    try {
      msg = JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return;
    }

    // ── Backend status / error ────────────────────────────────────────────
    if (msg.type === "status") {
      const state = msg.state as SessionState | undefined;
      if (state) this._setState(state);
      return;
    }
    if (msg.type === "error") {
      this.callbacks.onError(msg.message as string);
      this._setState("error");
      return;
    }

    // ── Gemini server messages ────────────────────────────────────────────
    const serverContent = msg.serverContent as Record<string, unknown> | undefined;

    if (serverContent) {
      // Audio delta
      const modelTurn = serverContent.modelTurn as Record<string, unknown> | undefined;
      if (modelTurn) {
        const parts = modelTurn.parts as Array<Record<string, unknown>> | undefined;
        parts?.forEach((part) => {
          const inlineData = part.inlineData as Record<string, unknown> | undefined;
          // Gemini reports e.g. "audio/pcm;rate=24000" — match on the base type only.
          const mimeType = typeof inlineData?.mimeType === "string" ? inlineData.mimeType : "";
          if (mimeType.split(";")[0].trim() === "audio/pcm" && typeof inlineData?.data === "string") {
            this.playback.enqueueBase64Chunk(inlineData.data);
          }
        });
      }

      // Turn complete signal
      if (serverContent.turnComplete === true) {
        this.playback.signalServerTurnComplete();
      }

      // Interrupted (barge-in)
      if (serverContent.interrupted === true) {
        this.playback.interrupt();
        this.playback.resetTurnState();
      }

      // Input transcription (user's speech)
      const inputTranscription = serverContent.inputTranscription as
        | Record<string, unknown>
        | undefined;
      if (inputTranscription?.text) {
        this.callbacks.onTranscript({
          role: "user",
          text: inputTranscription.text as string,
          timestamp: Date.now(),
        });
      }

      // Output transcription (agent's speech)
      const outputTranscription = serverContent.outputTranscription as
        | Record<string, unknown>
        | undefined;
      if (outputTranscription?.text) {
        this.callbacks.onTranscript({
          role: "agent",
          text: outputTranscription.text as string,
          timestamp: Date.now(),
        });
        this.playback.resetTurnState();
      }
    }

    // Session resumption handle — store for reconnect
    const resumption = msg.sessionResumptionUpdate as Record<string, unknown> | undefined;
    if (resumption?.newHandle) {
      sessionStorage.setItem(
        `gemini_handle_${this.agentId}`,
        resumption.newHandle as string
      );
    }
  }

  private _sendAudioChunk(pcmBuffer: ArrayBuffer): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    if (this.state !== "live" && this.state !== "ai_speaking") return;

    const uint8 = new Uint8Array(pcmBuffer);
    let binary = "";
    for (let i = 0; i < uint8.length; i++) {
      binary += String.fromCharCode(uint8[i]);
    }
    const base64 = btoa(binary);

    const message = {
      realtimeInput: {
        mediaChunks: [
          {
            mimeType: "audio/pcm;rate=16000",
            data: base64,
          },
        ],
      },
    };

    this.ws.send(JSON.stringify(message));
  }

  private _scheduleReconnect(): void {
    if (this.stopped) return;
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      this._setState("error");
      this.callbacks.onError("Reconnection failed after multiple attempts.");
      return;
    }

    this._setState("reconnecting");
    const delay =
      BASE_RECONNECT_DELAY_MS * Math.pow(2, this.reconnectAttempts);
    this.reconnectAttempts++;

    this.reconnectTimer = setTimeout(() => {
      this._connect();
    }, delay);
  }

  private _closeWs(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.onerror = null;
      try {
        this.ws.close(1000);
      } catch { /* ignore */ }
      this.ws = null;
    }
  }

  private _setState(state: SessionState): void {
    if (this.state === state) return;
    this.state = state;
    this.callbacks.onStateChange(state);
  }
}
